import React, { Component, memo } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import { RouteComponentProps } from 'react-router-dom';

import Page, { PageButtonLink } from '../../../../components/Page';
import Card from '../../../../components/Card';
import { LoadingOverlay } from '../../../../components/Loading';
import { updateParams, paramsEqual } from '../../../../helpers/urlParams';
import { AuthenticatedUser } from '../../../../auth/types';

import { State as PayoutRulesState } from '../reducer';
import PayoutRulesTable from './PayoutRulesTable';
import Filters from './Filters';
import ListMessage from './ListMessage';
import { GetPayoutRulesReason } from '../actions';
import { hasAnyOfScopes } from '../../../../auth/accessToken/Allow';
import Icon from '../../../../components/Icons';
import { H1 } from '../../../../components/Typography';
import { withAccountIds, AccountIdsProps } from '../../../../auth/accessToken/withAccountIds';
import { BillingPayoutRule } from '../../../../types/billing';
import { distances } from '../../../../styles/constants';

interface MatchParams {
    accountId: string;
}

export interface PayoutRulesListProps extends WithTranslation, RouteComponentProps<MatchParams>, AccountIdsProps {
    getPayoutRules: (accountId: string, params: URLSearchParams, origin: GetPayoutRulesReason) => void;
    payoutRules: PayoutRulesState;
    user: AuthenticatedUser;
    getCheckoutConfiguration: (accountIds: string[]) => void;
    clearData: () => void;
}

interface PayoutRulesListState {
    isSearching: boolean;
    timeoutReference: number | undefined;
}

type PayoutRulesAccountSectionProps = {
    payoutRules: BillingPayoutRule[];
    title: string;
    accountId: string;
};

const sortRules = (rules: BillingPayoutRule[]) => {
    const active = rules.filter((x) => !x.deleted_at);
    const deleted = rules.filter((x) => x.deleted_at);
    return [...active, ...deleted];
};

const PayoutRulesAccountSection = memo(({ title, payoutRules, accountId }: PayoutRulesAccountSectionProps) => {
    return (
        <MarginTopCard title={title} noPadding>
            <PayoutRulesTable payoutRules={sortRules(payoutRules)} accountId={accountId} />
        </MarginTopCard>
    );
});

class PayoutRulesList extends Component<PayoutRulesListProps, PayoutRulesListState> {
    componentDidMount() {
        const { limit, payoutRules, accountId, params } = this.props.payoutRules;
        const accountIds = this.props.accountIds;
        const user = this.props.user;
        const locationQueryParams = new URLSearchParams(this.props.location.search.substring(1));
        const requestQueryParams = updateParams(locationQueryParams, {
            limit: limit.toString(),
            include_deleted: true,
        });
        const urlAccountId = this.props.match.params.accountId;
        const hasPayoutRules = accountIds.map((id) => payoutRules[id]).flat().length > 0;
        const isSameAccount = accountId === urlAccountId;
        const hasSameParams = paramsEqual(requestQueryParams, params, ['limit', 'starting_after', 'include_deleted']);
        const reason =
            hasPayoutRules && isSameAccount && hasSameParams
                ? GetPayoutRulesReason.refresh
                : GetPayoutRulesReason.mount;
        accountIds.forEach((accountId) =>
            this.props.getPayoutRules(
                accountId,
                requestQueryParams,
                accountId.startsWith('T') ? GetPayoutRulesReason.testAccount : reason
            ));
        if (hasAnyOfScopes(urlAccountId, user, [['admin'], ['admin:admin'], ['admin:checkout']])) {
            this.props.getCheckoutConfiguration([urlAccountId]);
        }
    }

    componentDidUpdate(prevProps: PayoutRulesListProps, prevState: any) {
        if (this.props.location.search !== prevProps.location.search) {
            const { limit } = this.props.payoutRules;
            const locationQueryParams = new URLSearchParams(this.props.location.search.substring(1));
            const requestQueryParams = updateParams(locationQueryParams, {
                limit: limit.toString(),
                include_deleted: true,
            });
            this.props.accountIds.forEach((accountId) =>
                this.props.getPayoutRules(
                    accountId,
                    requestQueryParams,
                    accountId.startsWith('T') ? GetPayoutRulesReason.testAccount : GetPayoutRulesReason.update
                ));
        }
    }

    componentWillUnmount() {
        this.props.clearData();
    }

    render() {
        const { t, accountIds } = this.props;
        const { payoutRules, isLoading, checkoutConfigurations } = this.props.payoutRules;
        const accountId = this.props.match.params.accountId;
        const locationQueryParams = new URLSearchParams(this.props.location.search.substring(1));

        const prodAccount = accountIds.find((x) => x.startsWith('P'));
        const hasProdRules = !!prodAccount && (payoutRules[prodAccount] || []).length > 0;

        const testAccount = accountIds.find((x) => x.startsWith('T'));
        const hasTestRules = !!testAccount && (payoutRules[testAccount] || []).length > 0;

        return (
            <Page
                title={
                    <>
                        <Title>{t('payout_rules.payout_rules.title')}</Title>
                        <PageButtonLink to={`/${accountId}/settings/payout-rules/new`}>
                            <Icon icon="document" />
                            {t('payout_rules.payout_rules.create_new')}
                        </PageButtonLink>
                    </>
                }
                hasEnvFrame={false}
            >
                <Card noPadding>
                    <Filters
                        history={this.props.history}
                        location={this.props.location}
                        checkoutConfig={checkoutConfigurations[accountId]}
                    />
                </Card>
                {hasProdRules && (
                    <PayoutRulesAccountSection
                        title={t('environments.production')}
                        accountId={prodAccount}
                        payoutRules={payoutRules[prodAccount] || []}
                    />
                )}
                {hasTestRules && (
                    <PayoutRulesAccountSection
                        title={t('environments.test')}
                        accountId={testAccount}
                        payoutRules={payoutRules[testAccount] || []}
                    />
                )}
                {!hasProdRules && !hasTestRules && (
                    <MarginTopCard>
                        <RelativeGroup>
                            <ListMessage
                                hasRules={hasProdRules || hasTestRules}
                                isLoading={isLoading}
                                accountId={accountId}
                                queryParams={locationQueryParams}
                            />
                            {isLoading && <LoadingOverlay />}
                        </RelativeGroup>
                    </MarginTopCard>
                )}
            </Page>
        );
    }
}

export default withAccountIds(withTranslation()(PayoutRulesList));

const RelativeGroup = styled.div`
    position: relative;
    box-sizing: border-box;
    overflow: hidden;
`;

const Title = styled(H1)`
    display: inline-block;
`;
const MarginTopCard = styled(Card)`
    margin-top: ${distances.small};
`;
